import React, { useState, useEffect, useRef } from 'react';
import { FileText, Upload, FolderOpen, X, Check, Loader2, Link2, Download, ChevronRight, Home, Folder } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import mediaApi from '@/api/media';

/**
 * FilePicker - A reusable component for selecting/uploading files (PDFs, documents)
 * 
 * Features:
 * - Upload new files directly
 * - Select from Media Library with folder browsing
 * - Paste file URL
 * - Preview current file
 * 
 * @param {string} value - Current file URL
 * @param {function} onChange - Callback when file changes (receives URL string and filename)
 * @param {string} label - Optional label for the field
 * @param {string} placeholder - Placeholder text for URL input
 * @param {string} accept - File types to accept (default: ".pdf")
 * @param {string} className - Additional CSS classes
 * @param {string} folder - Media library folder for uploads
 * @param {string} testId - data-testid attribute
 */
export function FilePicker({
  value,
  onChange,
  label,
  placeholder = "Enter file URL or select from library",
  accept = ".pdf,application/pdf",
  className,
  folder = null,
  testId = "file-picker",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  const [mediaItems, setMediaItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  // Load media library when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadFolderContents(null);
      setUrlInput(value || '');
      setSelectedFile(null);
      setFolderPath([]);
      setCurrentFolder(null);
    }
  }, [isOpen]);

  const loadFolderContents = async (folderId) => {
    setLoading(true);
    try {
      // Load subfolders
      const foldersResult = await mediaApi.listFolders(folderId);
      if (foldersResult.success) {
        setFolders(foldersResult.data || []);
      }
      
      // Load documents in this folder
      const params = { media_type: 'document', limit: 100 };
      if (folderId) params.folder = folderId;
      const filesResult = await mediaApi.list(params);
      if (filesResult.success) {
        setMediaItems(filesResult.data || []);
      }
    } catch (error) {
      console.error('Failed to load media library:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  const navigateToFolder = (folder) => {
    if (folder === null) {
      setCurrentFolder(null);
      setFolderPath([]);
      loadFolderContents(null);
    } else {
      setCurrentFolder(folder);
      setFolderPath([...folderPath, folder]);
      loadFolderContents(folder.id);
    }
  };

  const navigateToBreadcrumb = (index) => {
    if (index === -1) {
      setCurrentFolder(null);
      setFolderPath([]);
      loadFolderContents(null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      const targetFolder = newPath[index];
      setCurrentFolder(targetFolder);
      setFolderPath(newPath);
      loadFolderContents(targetFolder.id);
    }
  };

  // Get folder display name (clean up GUID folders and show friendly names)
  const getFolderDisplayName = (folder) => {
    const name = folder.name || '';
    
    // Known folder types with friendly names
    const friendlyNames = {
      'pages': '📄 Landing Pages',
      'qr_codes': '📱 QR Codes', 
      'documents': '📁 Documents',
      'logos': '🏢 Logos',
      'agents': '👤 Agents',
      'images': '🖼️ Images',
    };
    
    if (friendlyNames[name]) {
      return friendlyNames[name];
    }
    
    // If it's a UUID-like name, try to make it more readable
    if (/^[a-f0-9-]{36}$/i.test(name)) {
      return `📄 ${name.slice(0, 8)}...`;
    }
    
    return name;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }

    setUploading(true);
    try {
      // Determine upload options
      const uploadOptions = {};
      if (currentFolder?.id) {
        uploadOptions.folder = currentFolder.id;
      } else if (folder) {
        // Check if folder looks like a path (contains /) or an ID (UUID-like)
        if (folder.includes('/') || !folder.match(/^[a-f0-9-]{36}$/i)) {
          uploadOptions.folder_path = folder;
        } else {
          uploadOptions.folder = folder;
        }
      }
      
      const result = await mediaApi.upload(file, uploadOptions);
      if (result.success && result.data) {
        const fileUrl = result.data.url || result.data.optimized_url;
        const fileName = result.data.original_filename || file.name;
        onChange(fileUrl, fileName);
        setIsOpen(false);
        toast.success('File uploaded successfully');
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSelect = () => {
    if (selectedFile) {
      const fileUrl = selectedFile.url || selectedFile.optimized_url;
      const fileName = selectedFile.original_filename || selectedFile.filename || 'Document';
      onChange(fileUrl, fileName);
      setIsOpen(false);
    } else if (urlInput) {
      const urlParts = urlInput.split('/');
      const fileName = urlParts[urlParts.length - 1] || 'Document';
      onChange(urlInput, fileName);
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange('', '');
  };

  const getDisplayName = () => {
    if (!value) return null;
    const parts = value.split('/');
    return decodeURIComponent(parts[parts.length - 1] || 'Document');
  };

  const displayName = getDisplayName();

  return (
    <div className={cn("space-y-2", className)} data-testid={testId}>
      {label && <Label>{label}</Label>}
      
      <div className="flex gap-2">
        <div className="flex-1 flex items-center gap-2 p-2 border rounded-md bg-muted/30">
          <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
          {value ? (
            <span className="text-sm truncate flex-1" title={displayName}>
              {displayName}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground flex-1">No file selected</span>
          )}
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleClear}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsOpen(true)}
          data-testid={`${testId}-browse`}
        >
          <FolderOpen className="w-4 h-4 mr-2" />
          Browse
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Select File</DialogTitle>
            <DialogDescription>
              Upload a new file or select from your media library
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="library" className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Library
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="library" className="mt-4">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center gap-1 mb-4 p-2 bg-muted/50 rounded-lg overflow-x-auto">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 px-2 shrink-0",
                    currentFolder === null && "bg-background shadow-sm"
                  )}
                  onClick={() => navigateToBreadcrumb(-1)}
                >
                  <Home className="h-4 w-4 mr-1" />
                  Root
                </Button>
                {folderPath.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 px-2 shrink-0",
                        index === folderPath.length - 1 && "bg-background shadow-sm"
                      )}
                      onClick={() => navigateToBreadcrumb(index)}
                    >
                      {getFolderDisplayName(folder)}
                    </Button>
                  </React.Fragment>
                ))}
              </div>

              <ScrollArea className="h-[400px] pr-4">
                {loading ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-4 gap-3">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton key={`folder-${i}`} className="h-12 rounded-lg" />
                      ))}
                    </div>
                    <div className="space-y-2">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton key={`file-${i}`} className="h-16" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Folders */}
                    {folders.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">Folders</p>
                        <div className="grid grid-cols-4 gap-3">
                          {folders.map((folder) => (
                            <button
                              key={folder.id}
                              type="button"
                              onClick={() => navigateToFolder(folder)}
                              className="flex items-center gap-2 p-3 rounded-lg border hover:border-primary hover:bg-muted/50 transition-colors text-left"
                            >
                              <Folder className="h-5 w-5 text-primary shrink-0" />
                              <span className="text-sm font-medium truncate">
                                {getFolderDisplayName(folder)}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Files */}
                    {mediaItems.length > 0 ? (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Documents {currentFolder && `in "${getFolderDisplayName(currentFolder)}"`}
                        </p>
                        <div className="space-y-2">
                          {mediaItems.map((item) => (
                            <div
                              key={item.id}
                              className={cn(
                                "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                                selectedFile?.id === item.id
                                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                  : "border-border hover:border-primary/50 hover:bg-muted/50"
                              )}
                              onClick={() => setSelectedFile(item)}
                            >
                              <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center shrink-0">
                                <FileText className="w-5 h-5 text-red-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {item.original_filename || item.filename}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {item.file_size ? `${(item.file_size / 1024).toFixed(1)} KB` : 'PDF'}
                                </p>
                              </div>
                              {selectedFile?.id === item.id && (
                                <Check className="w-5 h-5 text-primary shrink-0" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : folders.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                        <FileText className="w-12 h-12 mb-4 opacity-50" />
                        <p>No documents in library</p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => setActiveTab('upload')}
                        >
                          Upload a file
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <FileText className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-sm">No documents in this folder</p>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="upload" className="mt-4">
              <div className="flex flex-col items-center justify-center h-[400px] border-2 border-dashed rounded-lg">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={accept}
                  onChange={handleUpload}
                  className="hidden"
                  id="file-upload"
                />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Uploading...</p>
                  </div>
                ) : (
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-lg font-medium mb-1">Click to upload</p>
                    <p className="text-sm text-muted-foreground">PDF files only</p>
                    {currentFolder && (
                      <p className="text-xs text-primary mt-2">
                        Will upload to: {getFolderDisplayName(currentFolder)}
                      </p>
                    )}
                  </label>
                )}
              </div>
            </TabsContent>

            <TabsContent value="url" className="mt-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="file-url">File URL</Label>
                  <Input
                    id="file-url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder={placeholder}
                    className="mt-1"
                  />
                </div>
                {urlInput && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-red-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {urlInput.split('/').pop() || 'Document'}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{urlInput}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSelect}
              disabled={!selectedFile && !urlInput}
            >
              <Check className="w-4 h-4 mr-2" />
              Select File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FilePicker;
